import { MarkdownField } from '@/components/MarkdownField';
import { useDataProvider, useGetIdentity } from 'react-admin';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
} from '@mui/material';
import { Labeled } from 'react-admin';

type FormValues = {
  title: string;
  singleLineDescription: string;
  content: string;
};

type AppGlobalAlertEditDialogProps = {
  open: boolean;
  onClose: () => void;
  record: {
    id: unknown;
    title?: string;
    singleLineDescription?: string;
    content?: string;
  } | null;
  onSuccess: () => void;
};

export const AppGlobalAlertEditDialog = ({
  open,
  onClose,
  record,
  onSuccess,
}: AppGlobalAlertEditDialogProps) => {
  const dataProvider = useDataProvider();
  const userId = useGetIdentity().data?.id?.toString();

  const form = useForm<FormValues>({
    defaultValues: {
      title: '',
      singleLineDescription: '',
      content: '',
    },
  });

  const { reset, handleSubmit, formState } = form;

  useEffect(() => {
    if (open && record) {
      reset({
        title: record.title ?? '',
        singleLineDescription: record.singleLineDescription ?? '',
        content: record.content ?? '',
      });
    }
  }, [open, record, reset]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!record?.id) return;
      const now = new Date();
      await dataProvider.update('appGlobalAlerts', {
        id: record.id,
        data: {
          ...values,
          updatedBy: userId,
          updatedAt: now,
        },
        previousData: record,
      });
      onSuccess();
      onClose();
    },
    [dataProvider, record, userId, onSuccess, onClose],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Alert</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
              <Labeled label="Title" fullWidth>
                <MuiTextField
                  fullWidth
                  {...form.register('title', { required: 'Required' })}
                  error={!!formState.errors.title}
                  helperText={formState.errors.title?.message}
                />
              </Labeled>
              <Labeled label="Single Line Description" fullWidth>
                <MuiTextField
                  fullWidth
                  {...form.register('singleLineDescription', {
                    required: 'Required',
                  })}
                  error={!!formState.errors.singleLineDescription}
                  helperText={
                    formState.errors.singleLineDescription?.message
                  }
                />
              </Labeled>
              <MarkdownField label="Alert Content" source="content" />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formState.isSubmitting}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
